import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LanguageProvider } from '../../../../../core/language-provider';
import './style.css';

// Field type icons mapping (without fa- prefix for FontAwesomeIcon)
const FIELD_TYPE_ICONS = {
  string: 'font',
  text: 'align-left',
  richText: 'file-alt',
  number: 'hashtag',
  float: 'percentage',
  currency: 'dollar-sign',
  boolean: 'toggle-on',
  date: 'calendar',
  time: 'clock',
  datetime: 'calendar-alt',
  email: 'envelope',
  phone: 'phone',
  file: 'paperclip',
  image: 'image',
  option: 'list',
  externalEntity: 'link',
  deepExternalEntity: 'project-diagram',
  multipleExternalEntity: 'link',
  password: 'key'
};

// Field type labels
const FIELD_TYPE_LABELS = {
  string: 'Text',
  text: 'Long Text',
  richText: 'Rich Text',
  number: 'Number',
  float: 'Decimal',
  currency: 'Currency',
  boolean: 'Yes/No',
  date: 'Date',
  time: 'Time',
  datetime: 'Date & Time',
  email: 'Email',
  phone: 'Phone',
  file: 'File',
  image: 'Image',
  option: 'Option',
  externalEntity: 'Reference',
  deepExternalEntity: 'Nested',
  multipleExternalEntity: 'Multi-Ref',
  password: 'Password'
};

// Entities to exclude from browser
const EXCLUDED_ENTITIES = [
  'auth_pin',
  'auth_token',
  'user_entity_preferences',
  'user_entity_saved_filters',
  'resource'
];

export default function EntityBrowser({ entities, selectedEntity, onEntitySelect }) {
  const [expandedEntities, setExpandedEntities] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Get entity label helper
  const getEntityLabel = (entityName) => {
    return LanguageProvider.get(`entity.${entityName}`, entityName);
  };

  // Get sorted entity names (sorted by original name), excluding sensitive entities
  const entityNames = useMemo(() => {
    return Object.keys(entities)
      .filter(name => !EXCLUDED_ENTITIES.includes(name))
      .sort((a, b) => a.localeCompare(b));
  }, [entities]);

  // Filter entities by search term (searches both original names and translations)
  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) return entityNames;
    const term = searchTerm.toLowerCase();
    return entityNames.filter(entityName => {
      // Search in entity name and label
      if (entityName.toLowerCase().includes(term)) return true;
      const label = getEntityLabel(entityName);
      if (label.toLowerCase().includes(term)) return true;

      // Also search in field names and labels
      const entityProps = entities[entityName];
      if (entityProps?.columns) {
        return Object.keys(entityProps.columns).some(fieldName => {
          if (fieldName.toLowerCase().includes(term)) return true;
          const fieldLabel = LanguageProvider.getLabel(entityName, fieldName) || fieldName;
          return fieldLabel.toLowerCase().includes(term);
        });
      }
      return false;
    });
  }, [entityNames, entities, searchTerm]);

  // Toggle entity expansion
  const toggleEntity = (entityName) => {
    setExpandedEntities(prev => ({
      ...prev,
      [entityName]: !prev[entityName]
    }));
  };

  // Handle entity click
  const handleEntityClick = (entityName) => {
    onEntitySelect(entityName);
    setExpandedEntities(prev => ({
      ...prev,
      [entityName]: true
    }));
  };

  // Get fields for an entity
  const getEntityFields = (entityName) => {
    const entityProps = entities[entityName];
    if (!entityProps?.columns) return [];

    return Object.entries(entityProps.columns)
      .filter(([name, config]) => {
        // Exclude certain fields
        const excluded = ['deleted', 'password'];
        return !excluded.includes(name) && config.input_type !== 'password';
      })
      .map(([name, config]) => ({
        id: `${entityName}.${name}`,
        entity: entityName,
        name: name,
        label: LanguageProvider.getLabel(entityName, name) || name,
        type: config.input_type,
        options: config.options || {}
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  return (
    <div className="entity-browser">
      {/* Search */}
      <div className="entity-search">
        <div className="search-input-wrapper">
          <FontAwesomeIcon icon="search" className="search-icon" />
          <input
            type="text"
            placeholder="Cerca entita' o campi..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>
              <FontAwesomeIcon icon="times" />
            </button>
          )}
        </div>
      </div>

      {/* Entity Tree */}
      <div className="entity-tree">
        {filteredEntities.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon="database" />
            <p>Nessuna entita' trovata</p>
          </div>
        ) : (
          filteredEntities.map(entityName => {
            const isExpanded = expandedEntities[entityName];
            const isSelected = selectedEntity === entityName;
            const fields = getEntityFields(entityName);
            const entityLabel = getEntityLabel(entityName);

            return (
              <div key={entityName} className="entity-item">
                <div
                  className={`entity-header ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleEntityClick(entityName)}
                >
                  <span className="entity-expand" onClick={e => { e.stopPropagation(); toggleEntity(entityName); }}>
                    <FontAwesomeIcon icon={isExpanded ? 'chevron-down' : 'chevron-right'} />
                  </span>
                  <span className="entity-icon">
                    <FontAwesomeIcon icon="table" />
                  </span>
                  <span className="entity-name">
                    {entityName}
                    {entityLabel !== entityName && (
                      <span className="entity-label-translation">({entityLabel})</span>
                    )}
                  </span>
                  <span className="entity-count">{fields.length}</span>
                </div>

                {isExpanded && (
                  <div className="entity-fields">
                    {fields.map(field => (
                      <div
                        key={field.id}
                        className="field-item"
                      >
                        <span className="field-icon">
                          <FontAwesomeIcon icon={FIELD_TYPE_ICONS[field.type] || 'circle'} />
                        </span>
                        <span className="field-name">
                          {field.name}
                          {field.label !== field.name && (
                            <span className="field-label-translation">({field.label})</span>
                          )}
                        </span>
                        <span className="field-type">{FIELD_TYPE_LABELS[field.type] || field.type}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Info */}
      <div className="entity-browser-info">
        <FontAwesomeIcon icon="info-circle" />
        <span>Visualizza entità e campi disponibili</span>
      </div>
    </div>
  );
}
